import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AppLayout from '../../../../component/appLayout';
import { DocumentBlock, EditorContainer } from './styles';
import { getUrlParam, isEmpty } from '../../../../helpers/common';
import {
  createNewDoc,
  getDocumentData,
  getFileDetailsById,
  updateDocumentData,
  updateFileData
} from '../../../../services/projectServices';
import { captureException } from '../../../../services/logService';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';
import { COLOR_THEME_TYPE, FILE_TYPES, LINK_FILE_DATA, OLD_DOC_URL } from '../../../../global/constants';
import Modal from '../../../../component/modals/modal';
import Importmodal from '../../../../component/modals/importModal';
import DeleteModal from '../../../../component/modals/deleteModal';
import SharedocModal from '../../../../component/modals/shareDocmodal';
import VersionHistoryModal from '../../../../component/modals/versionHistoryModal';
import { REACT_APP_BLOCKSUITE_URL } from '../../../../global/environment';
import { getFirebaseToken } from '../../../../services/authServices';
import FilesModal from '../../../../component/modals/filesModal';
import { useMobileDevice } from '../../../../global/useMobile';
import { rootStore } from '../../../../mobx/rootStore';
import { RawProjectFile } from '../../../../mobx/interfaces/project';
import { ROUTES } from '../../../../routes/urls';
import { LiveUsersUpdateMessage, OnSaveMessage, ActiveUser } from '../../../../interfaces/ProjectInterface';
import DocHeader from './docHeader';
type MessageEventData = LiveUsersUpdateMessage | OnSaveMessage;

const CreateNewDoc: React.FC = () => {
  // Refs
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onSaveRef = useRef<((newContent: string) => Promise<void>) | null>(null);

  // States
  const [loading, setLoading] = useState(false);
  const [firebaseCustomToken, setFirebaseCustomToken] = useState('');
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [currentFileItem, setCurrentFileItem] = useState<RawProjectFile | null>(null);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [isVersionHistoryModelOpen, setIsVersionHistoryModelOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // mobx variable
  const { projectStore, workspaceStore, appStore } = rootStore;
  const { docData } = projectStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { themeMode } = appStore || {};

  // Other variables
  const history = useHistory();
  const mobile = useMobileDevice();
  const isDarkMode = themeMode.theme === COLOR_THEME_TYPE.DARK;

  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  const params: { docid: string; id: string } = useParams();
  const folderId = getUrlParam(history.location.search, 'folder');
  const docUrl = REACT_APP_BLOCKSUITE_URL || OLD_DOC_URL;

  // for handle import click
  const handleImportClick = useCallback(() => {
    setIsImportModalOpen(true);
  }, []);

  // for handle close import model
  const handleImportModalClose = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  // for open delete model
  const openDeleteModel = useCallback(() => {
    setDeleteModelOpen(true);
  }, []);

  // for close delete model
  const closeDeleteModel = useCallback(() => {
    setDeleteModelOpen(false);
  }, []);

  // for open version history model
  const openVersionHistoryModel = useCallback(() => {
    setIsVersionHistoryModelOpen(true);
  }, []);

  // for close version history model
  const closeVersionHistoryModel = useCallback(() => {
    setIsVersionHistoryModelOpen(false);
  }, []);

  // load initial data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (workspace?.id) {
        const firebaseToken = await getFirebaseToken(workspace?.id);
        setFirebaseCustomToken(firebaseToken);
        if (!isEmpty(params?.docid)) {
          await getDocumentData(params?.docid);
        }
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, params?.docid]);

  // for handle save/update button
  const handleSave = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
    iframeRef.current?.contentWindow?.postMessage({ type: 'save' }, OLD_DOC_URL);
  }, []);

  // Effect: load data when workspace changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // for clear data
  const clearData = useCallback(() => {
    rootStore.projectStore.setDocData({});
  }, []);

  // for handle cancel icon
  const onCancel = useCallback(() => {
    if (mobile) {
      history.goBack();
    } else if (folderId) {
      history.push(`${ROUTES.PROJECT_FILES.replace(':id', params?.id || '')}?folder=${folderId || ''}`);
    } else {
      history.goBack();
    }
    clearData();
  }, [clearData, folderId, history, mobile, params?.id]);

  // for open share model
  const openShareModel = useCallback(() => {
    setShareModalOpen(true);
  }, []);

  // for close share model
  const onCloseShareModal = useCallback(() => {
    setShareModalOpen(false);
  }, []);

  // for fave document
  const onSave = useCallback(
    async (newContent: string) => {
      try {
        setLoading(true);

        let response;
        if (!isEmpty(docData?.id)) {
          const payload = {
            Content: newContent,
            Name: docData?.name
          };
          response = await updateDocumentData(docData?.id, payload);
          if (response) rootStore.alertNotificationStore.setSuccessMessage('Document Updated!');
        } else {
          const payload = {
            Content: docData?.content,
            Name: docData?.name,
            ProjectId: params?.id,
            UploadFileType: FILE_TYPES.DOCS,
            ParentId: folderId ? folderId : null
          };
          response = await createNewDoc(payload);
          if (response) rootStore.alertNotificationStore.setSuccessMessage('Document Saved!');
        }
        if (response) {
          onCancel();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [params?.id, docData, folderId, onCancel]
  );

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  // Clear doc data only on unmount
  useEffect(() => {
    return () => {
      rootStore.projectStore.setDocData({});
    };
  }, []);

  // Effect: register message listener for live user updates and save events
  useEffect(() => {
    let didAddListener = false;
    const listener = (ev: MessageEvent<MessageEventData>) => {
      const { type, payload } = ev.data;

      if (type === 'live_users_update') {
        const liveUsers = payload.map((user: { userId: string }) => user.userId);

        if (!liveUsers.includes(userDetails.id)) {
          liveUsers.push(userDetails.id);
        }

        const updatedActiveUsers = liveUsers.map((userId: string) => {
          const userData = workspace?.users?.find((user) => user?.id === userId);
          return {
            id: userId,
            name: userData?.name || 'Unknown',
            profile: userData?.profile_photo || undefined
          };
        });

        setActiveUsers(updatedActiveUsers);
      }

      if (type === 'on_save') {
        onSaveRef.current?.(payload);
      }
    };

    if ((workspace?.users?.length || 0) > 0) {
      window.addEventListener('message', listener);
      didAddListener = true;
    }

    return () => {
      if (didAddListener) window.removeEventListener('message', listener);
    };
  }, [workspace?.users, userDetails?.id]);

  // for delete file
  const deleteFile = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setDeleteLoading(true);
        const payload = {
          IsArchived: true
        };
        const result = await updateFileData(docData?.id, payload);
        setDeleteModelOpen(false);
        if (result) {
          if (folderId) {
            history.push(`${ROUTES.PROJECT_FILES.replace(':id', params?.id || '')}?folder=${folderId}`);
          } else {
            history.push(ROUTES.PROJECT_FILES.replace(':id', params?.id || ''));
          }
        }
      } catch (error) {
        captureException(error);
      } finally {
        setDeleteLoading(false);
      }
    },
    [docData?.id, folderId, history, params?.id]
  );

  // for open rename model
  const openRenameModal = useCallback(async () => {
    const result = await getFileDetailsById(docData?.id);
    setCurrentFileItem(result);
    setIsFolderModalOpen(true);
  }, [docData?.id]);

  // for close rename model
  const closeRenameModal = useCallback(() => {
    setIsFolderModalOpen(false);
  }, []);

  // for update document name
  const updateDocName = useCallback(
    (name: string) => {
      const updatedData = { ...docData, name };
      rootStore.projectStore.setDocData(updatedData);
    },
    [docData]
  );

  // for iframe load
  const handleIframeLoad = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'load_doc', payload: docData?.content },
      docData?.isBlocksuite ? `${REACT_APP_BLOCKSUITE_URL}` : OLD_DOC_URL
    );
  }, [docData?.content, docData?.isBlocksuite]);

  // handle full width toggle
  const handleToggle = useCallback(() => {
    const newValue = !isChecked;
    setIsChecked(newValue);
    const newWidth = newValue ? '100%' : '944px';
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'update-editor-width', payload: newWidth },
      `${REACT_APP_BLOCKSUITE_URL}`
    );
  }, [isChecked]);

  return (
    <>
      <AppLayout>
        <DocumentBlock isEdge={docData?.isEdge} className='desktop-doc-scroll'>
          <DocHeader
            docData={docData}
            loading={loading}
            isChecked={isChecked}
            activeUsers={activeUsers}
            editorIframe={iframeRef}
            onCancel={onCancel}
            handleSave={handleSave}
            handleToggle={handleToggle}
            openShareModel={openShareModel}
            openRenameModal={openRenameModal}
            openDeleteModel={openDeleteModel}
            openVersionHistoryModel={openVersionHistoryModel}
            handleImportClick={handleImportClick}
          />
          {!loading && !isEmpty(userDetails?.id) && !isEmpty(workspace?.id) && !isEmpty(firebaseCustomToken) && (
            <EditorContainer className='editor' isEdge={docData?.isEdge}>
              <iframe
                ref={iframeRef}
                src={`${!docData?.isBlocksuite ? OLD_DOC_URL : docUrl}${
                  docData?.id && !docData?.isBlocksuite ? '/editorjs' : ''
                }?docId=${params?.docid}&firebase_token=${firebaseCustomToken}&workspace_id=${workspace?.id}&theme=${
                  isDarkMode ? 'dark' : 'light'
                }&userId=${userDetails.id}&userName=${userDetails.given_name}&isEdge=${docData?.isEdge}`}
                width={'100%'}
                title='teamcamp doc'
                className='docs-section white-board'
                allow='clipboard-read; clipboard-write'
                onLoad={handleIframeLoad}
              />
            </EditorContainer>
          )}
        </DocumentBlock>
      </AppLayout>
      <Modal open={isImportModalOpen} onClose={handleImportModalClose} maxWidth={'400px'}>
        <Importmodal onClose={handleImportModalClose} editorIframe={iframeRef} />
      </Modal>
      <Modal open={deleteModelOpen} onClose={closeDeleteModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteModel}
          onOk={deleteFile}
          loading={deleteLoading}
          modalTitle={`Delete Folder`}
          description={`Are you sure you want to delete this Document?`}
        />
      </Modal>
      <Modal open={shareModalOpen} onClose={onCloseShareModal} maxWidth={'462px'}>
        <SharedocModal
          onClickCancel={onCloseShareModal}
          editorIframe={iframeRef}
          isEdge={docData?.isEdge || false}
          docTitle={docData?.name || ''}
          docId={docData?.id || ''}
        />
      </Modal>
      <Modal open={isFolderModalOpen} onClose={closeRenameModal} maxWidth={'462px'}>
        <FilesModal
          onClose={closeRenameModal}
          itemData={currentFileItem}
          updateDocName={updateDocName}
          modalData={LINK_FILE_DATA.FOLDER}
        />
      </Modal>
      <Modal
        open={isVersionHistoryModelOpen}
        onClose={closeVersionHistoryModel}
        maxWidth={'770px'}
        isResponsiveMaxWidth={true}>
        <VersionHistoryModal
          mainIframe={iframeRef}
          docUrl={docUrl}
          docData={docData}
          onClose={closeVersionHistoryModel}
        />
      </Modal>
    </>
  );
};

export default CreateNewDoc;
